import React, { useRef } from 'react';

import SearchableMenu, {
  TItemToString,
} from 'components/base/Menu/SearchableMenu';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import AnimatedCaret from 'components/base/AnimatedCaret';
import CustomerIcon from '../CustomerIcon/CustomerIcon';
import { RecentFilter } from 'components/TopicFilters/TopicFilters.styles';
import colors from 'styles/themes/colors';
import { ThemeProps } from 'types/styled';
import styled, { css } from 'styled-components/macro';
import variables from 'styles/themes/variables';
import modularScale from 'styles/helpers/modular-scale';
import { useFilterSize } from 'components/TopicFilters/utils/useFilterSize';

const hoveredStyles = (isActive: any): any => css`
  background: ${colors.Sand};
  span {
    transform: ${!isActive && 'translateX(5px)'};
  }
`;

const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${hoveredStyles(isActive)}
    }

    ${isHovered && hoveredStyles(isActive)}
  `}
`;

const ButtonBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isOpen, isSubscriptionForm }): any => css`
    @media screen and (max-width: 47em) {
      background: ${isOpen && !isSubscriptionForm ? colors.Sand : 'white'};

      padding: ${({ theme }): string =>
        `${modularScale(-2)} ${theme.spacing.medium} ${modularScale(
          -2
        )} 0.75rem `};
    }
  `}
`;

const Button: React.FC<IButtonProps> = (props) => {
  const { selectedItem, isOpen, disabled, defaultLabel, isSubscriptionForm } =
    props;
  const ref = useRef(null);

  const boxColor = disabled ? colors.Grey : 'black';

  useFilterSize(ref);

  return (
    <ButtonBox
      isOpen={isOpen}
      bg="white"
      py={1}
      px={2}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      color={boxColor}
      isSubscriptionForm={isSubscriptionForm}
    >
      <Box
        alignSelf="auto"
        display="inline-flex"
        minWidth="1.5rem"
        height="15px"
        maxHeight="1rem"
      >
        {selectedItem?.name && (
          <CustomerIcon
            small
            aria-hidden
            customer={selectedItem.name}
            isDownloadsDsp={selectedItem.feedType === 'downloads'}
          />
        )}
      </Box>
      <Span
        alignSelf="auto"
        marginRight="8px"
        fontWeight={600}
        color={colors.NightBlue}
        ref={ref}
      >
        {selectedItem?.label || defaultLabel}
      </Span>
      <Span marginLeft="auto" alignSelf="auto" color={colors.NightBlue}>
        <AnimatedCaret isPointingUp={isOpen} />
      </Span>
    </ButtonBox>
  );
};

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, isHovered } = props;

  return (
    <ListItemBox
      isActive={isActive}
      isHovered={isHovered}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      py={2}
      paddingRight={2}
      paddingLeft={3}
    >
      <Box
        alignSelf="auto"
        display="inline-block"
        minWidth="2rem"
        maxHeight="1rem"
      >
        {item.name && (
          <CustomerIcon
            small
            aria-hidden
            customer={item.name}
            isDownloadsDsp={item.feedType === 'downloads'}
          />
        )}
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {item.label}
      </Span>
      <RecentFilter>{item.isRecent ? 'Recent' : ''}</RecentFilter>
    </ListItemBox>
  );
};

const getButtonWithDefaultLabel = (defaultLabel?: string): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return <Button {...props} defaultLabel={defaultLabel} />;
  };
  return LabeledButton;
};

const CustomerDropdown: React.FC<ICustomerDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    onChange,
    disabled,
    disabledMessage,
    itemToString,
    defaultLabel,
    children,
    isTooltipEnabled,
    tooltipContent,
    isSubscriptionForm = false,
    ListLabel,
  } = props;

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <div>
      <SearchableMenu
        items={items}
        selectedItem={selectedItem || null}
        onChange={onChange}
        disabled={disabled}
        disabledMessage={disabledMessage}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(defaultLabel)}
        Item={ListItem}
        isTooltipEnabled={isTooltipEnabled}
        tooltipContent={tooltipContent}
        isSubscriptionForm={isSubscriptionForm}
        ListLabel={ListLabel}
        showListLabel={!!ListLabel}
      >
        {children}
      </SearchableMenu>
    </div>
  );
};

interface ICustomerDropdownProps {
  items: any[];
  selectedItem: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  disabledMessage?: string;
  itemToString?: TItemToString;
  defaultLabel?: string;
  children?: any;
  isTooltipEnabled?: boolean;
  tooltipContent?: string | JSX.Element;
  isSubscriptionForm?: boolean;
  ListLabel?: any;
}

interface IButtonProps {
  selectedItem: any;
  isOpen?: boolean;
  disabled?: boolean;
  defaultLabel?: string;
  isSubscriptionForm?: boolean;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
}

export default CustomerDropdown;
